import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Shield } from 'lucide-react';
import '@/App.css';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import HomePage from './pages/HomePage';
import LandingPage from './pages/LandingPage';
import TradePage from './pages/TradePage';
import AdvancedTradePage from './pages/AdvancedTradePage';
import AICoachPage from './pages/AIPage';
import OrdersPage from './pages/OrdersPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import StockDetailPage from './pages/StockDetailPage';
import ScannersPage from './pages/ScannersPage';
import ScreenerPage from './pages/ScreenerPage';
import ProfilePage from './pages/ProfilePage';
import TradeHistoryPage from './pages/TradeHistoryPage';
import TradeDetailPage from './pages/TradeDetailPage';
import TradingAnalyticsPage from './pages/TradingAnalyticsPage';
import DiscoverPage from './pages/DiscoverPage';
import DisciplinePage from './pages/DisciplinePage';
import AlgoFlowsPage from './pages/AlgoFlowsPage';
import MarketsPage from './pages/MarketsPage';
import BugReportsPage from './pages/BugReportsPage';
import AdminPage from './pages/AdminPage';
import TradePerformancePage from './pages/TradePerformancePage';
import DocsPage from './pages/docs/DocsPage';
import JourneyPage from './pages/JourneyPage';
import GuidePage from './pages/GuidePage';
import TradePlanningPage from './pages/TradePlanningPage';
import TradeWalkthroughPage from './pages/TradeWalkthroughPage';
import CalculatorPage from './pages/CalculatorPage';
import GlobalCuesPage from './pages/GlobalCuesPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CapitalPage from './pages/CapitalPage';
import MarketAnalysisPage from './pages/MarketAnalysisPage';
import DerivativesPage from './pages/DerivativesPage';
import ForumPage from './pages/ForumPage';
import ForumPostPage from './pages/ForumPostPage';
import ForumCreatePage from './pages/ForumCreatePage';
import MentorshipPage from './pages/MentorshipPage';
import StudentProfilePage from './pages/StudentProfilePage';
import LearningSection_v2 from './components/LearningSection_v2';
import { ActModule } from './components/learning/ActModule';
import Layout from './components/Layout';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SocketProvider } from './context/SocketContext';
import { usePermissions } from './hooks/usePermissions';
import UpgradeModal from './components/UpgradeModal';

// Guided tour
import { TourProvider } from './tour/TourContext';
import TourRunner from './tour/TourRunner';
import TourLauncherModal from './tour/TourLauncherModal';

function RiskProfileGate({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!loading && user && !user.questionnaire_completed && !location.pathname.startsWith('/profile')) {
    return (
      <>
        {children}
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm pb-6 px-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#6366f120' }}>
                <Shield className="w-6 h-6" style={{ color: '#818cf8' }} />
              </div>
              <h2 className="text-white font-semibold text-lg">Complete Your Risk Profile</h2>
              <p className="text-gray-400 text-sm">
                TradeReflex personalises your capital limits, trade sizing, and AI review based on your risk profile. It takes under 2 minutes.
              </p>
              <ul className="text-left w-full mt-1 space-y-1">
                {['Capital tier & daily limits', 'Trade size recommendations', 'AI review personalisation', 'Discipline score baseline'].map(f => (
                  <li key={f} className="flex items-center gap-2 text-gray-300 text-sm">
                    <span style={{ color: '#818cf8' }}>✓</span> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/profile?section=risk-profile&locked=1')}
                className="mt-2 w-full py-2.5 rounded-xl text-white text-sm font-semibold transition"
                style={{ background: 'linear-gradient(135deg, #6366f1, #818cf8)', boxShadow: '0 4px 14px rgba(99,102,241,0.35)' }}
              >
                Set Up Risk Profile
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return children;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;

  return <RiskProfileGate>{children}</RiskProfileGate>;
}

function PlanRoute({ children, minPlan }) {
  const { meetsMinPlan, plan } = usePermissions();
  const [showModal, setShowModal] = React.useState(!meetsMinPlan(minPlan));

  if (!meetsMinPlan(minPlan)) {
    return (
      <>
        <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center opacity-30 pointer-events-none select-none">
          {children}
        </div>
        {showModal && (
          <UpgradeModal
            requiredPlan={minPlan}
            message={`Your current plan (${plan}) does not include access to this feature.`}
            onClose={() => setShowModal(false)}
          />
        )}
      </>
    );
  }

  return children;
}

function ActModulePage() {
  return <ActModule onBack={() => window.history.back()} />;
}

function PublicHomeRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  // If authenticated, show dashboard
  if (user) {
    return (
      <Layout>
        <HomePage />
      </Layout>
    );
  }

  // If not authenticated, show landing page
  return <LandingPage />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/" element={<PublicHomeRoute />} />
      <Route path="/trade" element={
        <PrivateRoute>
          <Layout>
            <TradePage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/trade/order" element={
        <PrivateRoute>
          <Layout>
            <AdvancedTradePage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/discover" element={
        <PrivateRoute>
          <Layout>
            <DiscoverPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/trading-analytics" element={
        <PrivateRoute>
          <PlanRoute minPlan="premium">
            <Layout>
              <TradingAnalyticsPage />
            </Layout>
          </PlanRoute>
        </PrivateRoute>
      } />
      <Route path="/discipline" element={
        <PrivateRoute>
          <Layout>
            <DisciplinePage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/analytics" element={
        <PrivateRoute>
          <PlanRoute minPlan="premium">
            <Layout>
              <TradingAnalyticsPage />
            </Layout>
          </PlanRoute>
        </PrivateRoute>
      } />
      <Route path="/scanners" element={
        <PrivateRoute>
          <Layout>
            <ScannersPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/screener" element={
        <PrivateRoute>
          <Layout>
            <ScreenerPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/ai-coach" element={
        <PrivateRoute>
          <PlanRoute minPlan="premium">
            <Layout>
              <AICoachPage />
            </Layout>
          </PlanRoute>
        </PrivateRoute>
      } />
      <Route path="/orders" element={
        <PrivateRoute>
          <Layout>
            <OrdersPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/profile" element={
        <PrivateRoute>
          <Layout>
            <ProfilePage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/stock/:symbol" element={
        <PrivateRoute>
          <Layout>
            <StockDetailPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/trade-history" element={
        <PrivateRoute>
          <Layout>
            <TradeHistoryPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/trade/:tradeId" element={
        <PrivateRoute>
          <Layout>
            <TradeDetailPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/risk-profile" element={<Navigate to="/profile?section=risk-profile" replace />} />
      <Route path="/trade-planning" element={
        <PrivateRoute>
          <Layout>
            <TradePlanningPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/trade-walkthrough" element={
        <PrivateRoute>
          <Layout>
            <TradeWalkthroughPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/learning" element={
        <PrivateRoute>
          <Layout>
            <LearningSection_v2 />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/act" element={
        <PrivateRoute>
          <Layout>
            <ActModulePage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/algo-flows" element={
        <PrivateRoute>
          <PlanRoute minPlan="premium">
            <Layout>
              <AlgoFlowsPage />
            </Layout>
          </PlanRoute>
        </PrivateRoute>
      } />
      <Route path="/capital" element={
        <PrivateRoute>
          <Layout>
            <CapitalPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/bug-reports" element={
        <PrivateRoute>
          <Layout>
            <BugReportsPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/admin" element={
        <PrivateRoute>
          <PlanRoute minPlan="admin">
            <Layout>
              <AdminPage />
            </Layout>
          </PlanRoute>
        </PrivateRoute>
      } />
      <Route path="/markets" element={
        <PrivateRoute>
          <Layout>
            <MarketsPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/derivatives" element={
        <PrivateRoute>
          <PlanRoute minPlan="premium">
            <Layout>
              <DerivativesPage />
            </Layout>
          </PlanRoute>
        </PrivateRoute>
      } />
      <Route path="/trade-performance" element={
        <PrivateRoute>
          <Layout>
            <TradePerformancePage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/personality" element={<Navigate to="/trade-performance" replace />} />
      {/* Trade Mode nav shortcuts */}
      <Route path="/nyx" element={<Navigate to="/ai-coach" replace />} />
      {/* /backtest → opens ProfilePage directly on the Backtest Lab section */}
      <Route path="/backtest" element={
        <PrivateRoute>
          <PlanRoute minPlan="premium">
            <Layout>
              <ProfilePage initialSection="backtest" />
            </Layout>
          </PlanRoute>
        </PrivateRoute>
      } />
      <Route path="/docs" element={
        <PrivateRoute>
          <Layout>
            <DocsPage onBack={() => window.history.back()} />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/journey" element={
        <PrivateRoute>
          <Layout>
            <JourneyPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/appguide" element={
        <PrivateRoute>
          <Layout>
            <GuidePage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/guide" element={<Navigate to="/appguide" replace />} />
      <Route path="/global-cues" element={
        <PrivateRoute>
          <Layout>
            <GlobalCuesPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/calculator" element={
        <PrivateRoute>
          <Layout>
            <CalculatorPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/market-analysis" element={
        <PrivateRoute>
          <PlanRoute minPlan="premium">
            <Layout>
              <MarketAnalysisPage />
            </Layout>
          </PlanRoute>
        </PrivateRoute>
      } />
      <Route path="/forum" element={
        <PrivateRoute>
          <Layout>
            <ForumPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/forum/create" element={
        <PrivateRoute>
          <Layout>
            <ForumCreatePage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/forum/:postId" element={
        <PrivateRoute>
          <Layout>
            <ForumPostPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/mentorship" element={
        <PrivateRoute>
          <Layout>
            <MentorshipPage />
          </Layout>
        </PrivateRoute>
      } />
      <Route path="/mentorship/students/:studentId" element={
        <PrivateRoute>
          <Layout>
            <StudentProfilePage />
          </Layout>
        </PrivateRoute>
      } />
      {/* Redirect old routes */}
      <Route path="/ai" element={<Navigate to="/ai-coach" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SocketProvider>
          <ThemeProvider>
            <BrowserRouter>
              <TourProvider>
                <div className="App">
                  <ErrorBoundary inline message="This page crashed unexpectedly. Tap Retry or navigate away.">
                    <AppRoutes />
                  </ErrorBoundary>
                  <Toaster position="top-center" toastOptions={{ style: { marginTop: 'env(safe-area-inset-top)' } }} />
                  <TourRunner />
                  <TourLauncherModal />
                </div>
              </TourProvider>
            </BrowserRouter>
          </ThemeProvider>
        </SocketProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
